import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  PublicStripeInvite as Invite, getPublicStripeInvite, startStripeInviteOnboarding,
} from '../api.publicStripeInvite';
import { useLang } from '../i18n/LanguageContext';
import { publicStripeInviteText } from '../i18n/pages/publicStripeInvite';

/**
 * Where an invited apartment owner connects their own Stripe account.
 *
 * Public and standalone: the owner has no Guestbook account and never gets one, so there is no
 * navigation, no login prompt, and nothing else of the app reachable from here. The token in the
 * URL is the entire authorisation.
 *
 * Stripe returns the browser here (?stripe=return) whether or not onboarding actually succeeded,
 * so the page re-fetches rather than trusting the redirect.
 */

/** Where a Czech apartment's owner plausibly banks, plus the larger EEA markets. Names come from
 * Intl rather than the translation files — 20+ country names in 4 languages is exactly the sort of
 * table that rots, and the browser already has them. */
const COUNTRY_CODES = [
  'CZ', 'SK', 'PL', 'DE', 'AT', 'HU', 'RO', 'BG', 'HR', 'SI',
  'IT', 'ES', 'FR', 'NL', 'BE', 'PT', 'SE', 'DK', 'FI', 'IE', 'GB', 'US',
];

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 16, padding: '32px 26px',
  boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 12px 32px -16px rgba(15,23,42,.25)',
  maxWidth: 480, width: '100%', textAlign: 'center',
};
const heading: React.CSSProperties = {
  fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', margin: '0 0 10px', lineHeight: 1.25,
};
const sub: React.CSSProperties = { fontSize: '0.9rem', color: '#475569', margin: '0 0 20px' };

const PublicStripeInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { lang } = useLang();
  const t = publicStripeInviteText[lang];

  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [country, setCountry] = useState('CZ');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stripe bounces the browser back here on both success and abandonment, so its presence only
  // means "they've been away", never "it worked".
  const returnedFromStripe = searchParams.get('stripe') !== null;

  const countryNames = useMemo(() => {
    let display: Intl.DisplayNames | null = null;
    try {
      display = new Intl.DisplayNames([lang], { type: 'region' });
    } catch {
      display = null;
    }
    return COUNTRY_CODES
      .map(code => ({ code, name: display?.of(code) || code }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [lang]);

  useEffect(() => {
    if (!token) { setInvalid(true); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicStripeInvite(token);
        if (cancelled) return;
        setInvite(data);
        setCountry(data.defaultCountry || 'CZ');
      } catch {
        // Expired, revoked, already spent, never existed — the server reports them identically
        // on purpose, and the copy is the same for all of them anyway.
        if (!cancelled) setInvalid(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleStart = async () => {
    if (!token) return;
    setStarting(true);
    setError(null);
    try {
      const { url } = await startStripeInviteOnboarding(token, country);
      window.location.href = url;
    } catch (err: any) {
      setError(err?.response?.data?.message || t.startError);
      setStarting(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div style={{
      minHeight: '100vh', background: '#f1f5f9', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      <div style={card}>
        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#94a3b8', marginBottom: 22 }}>
          Guestbook
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) return shell(<p style={sub}>{t.loading}</p>);

  if (invalid || !invite) {
    return shell(<>
      <h1 style={heading}>{t.invalidTitle}</h1>
      <p style={{ ...sub, margin: 0 }}>{t.invalidBody}</p>
    </>);
  }

  if (invite.status === 'ALREADY_CONNECTED') {
    return shell(<>
      <h1 style={heading}>{t.connectedTitle}</h1>
      <p style={sub}>{t.connectedBody}</p>
      <div style={{
        background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
        borderRadius: 10, padding: '12px 14px', fontSize: '0.84rem', textAlign: 'left',
      }}>
        {t.connectedDashboard}
      </div>
    </>);
  }

  const forWhat = invite.apartmentNames.length > 0
    ? invite.apartmentNames.join(', ')
    : invite.paymentMethodName;

  return shell(<>
    {returnedFromStripe && (
      <div style={{
        background: '#fefce8', border: '1px solid #fde047', color: '#854d0e',
        borderRadius: 10, padding: '12px 14px', fontSize: '0.84rem',
        textAlign: 'left', marginBottom: 20,
      }}>
        <strong style={{ display: 'block', marginBottom: 4 }}>{t.unfinishedTitle}</strong>
        {t.unfinishedBody}
      </div>
    )}

    <h1 style={heading}>{t.invitedBy.replace('{manager}', invite.managerName)}</h1>
    <p style={sub}>
      {t.forWhat.replace('{what}', forWhat)}
      <br />
      {t.noAccountNeeded}
    </p>

    <ul style={{
      textAlign: 'left', background: '#f8fafc', borderRadius: 12, padding: '14px 16px 14px 32px',
      margin: '0 0 20px', fontSize: '0.84rem', color: '#475569', lineHeight: 1.55,
    }}>
      <li style={{ marginBottom: 6 }}>{t.termOwnAccount}</li>
      <li style={{ marginBottom: 6 }}>{t.termFees}</li>
      <li>{t.termVisibility.replace('{manager}', invite.managerName)}</li>
    </ul>

    <div style={{ textAlign: 'left', marginBottom: 18 }}>
      <label htmlFor="country" style={{
        display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 5,
      }}>
        {t.countryLabel}
      </label>
      <select
        id="country"
        value={country}
        onChange={e => setCountry(e.target.value)}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 9,
          border: '1px solid #cbd5e0', fontSize: '0.9rem', background: '#fff', color: '#0f172a',
        }}
      >
        {countryNames.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
      </select>
    </div>

    {error && (
      <div style={{
        background: '#fef2f2', color: '#dc2626', borderRadius: 9,
        padding: '10px 13px', marginBottom: 16, fontSize: '0.84rem', textAlign: 'left',
      }}>
        {error}
      </div>
    )}

    <button
      onClick={handleStart}
      disabled={starting}
      style={{
        background: '#635bff', color: '#fff', border: 'none', borderRadius: 10,
        padding: '12px 26px', fontWeight: 700, fontSize: '0.95rem',
        cursor: starting ? 'not-allowed' : 'pointer', opacity: starting ? 0.7 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 9,
      }}
    >
      {starting && (
        <span style={{
          width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent',
          borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite',
        }} />
      )}
      {starting ? t.redirecting : (returnedFromStripe ? t.continueCta : t.connectCta)}
    </button>

    <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: '14px 0 0' }}>{t.countryHint}</p>
  </>);
};

export default PublicStripeInvite;
